/**
 * Client-only error codes: browser/WebAuthn failures that never reach the server, so they
 * don't belong in shared-types' `ErrorCode` (which is the server's response vocabulary).
 */
export const ClientErrorCode = {
    PASSKEY_CANCELLED: 'PASSKEY_CANCELLED',
    PASSKEY_ALREADY_ON_DEVICE: 'PASSKEY_ALREADY_ON_DEVICE',
    PASSKEY_AUTHENTICATOR_FULL_OR_UNSUPPORTED: 'PASSKEY_AUTHENTICATOR_FULL_OR_UNSUPPORTED',
    PASSKEY_RP_ID_MISMATCH: 'PASSKEY_RP_ID_MISMATCH',
    PASSKEY_UNSUPPORTED: 'PASSKEY_UNSUPPORTED',
    PASSKEY_NO_CREDENTIAL_RETURNED: 'PASSKEY_NO_CREDENTIAL_RETURNED',
    NETWORK_ERROR: 'NETWORK_ERROR',
};
export class AuthClientError extends Error {
    code;
    status;
    details;
    constructor(code, message, status, details) {
        super(message);
        this.name = 'AuthClientError';
        this.code = code;
        this.status = status;
        this.details = details;
    }
}
/**
 * Maps a browser-thrown WebAuthn error (from `@simplewebauthn/browser`'s `startRegistration`/
 * `startAuthentication`) to an `AuthClientError` with a client-only code, per the table in the
 * build plan §5.
 */
export function mapWebAuthnError(err, ceremony) {
    if (!(err instanceof Error)) {
        return new AuthClientError(ClientErrorCode.PASSKEY_UNSUPPORTED, 'Unknown WebAuthn error', 0);
    }
    const webAuthnErr = err;
    // `startAuthentication()`/`startRegistration()` throw this exact plain `Error` (no `.code`,
    // so it's not something `identifyAuthenticationError()` classified) when
    // `navigator.credentials.get()`/`.create()` *resolves* with no credential rather than
    // rejecting. Real user cancellation or timeout rejects with `NotAllowedError` instead — this
    // path is what a locked competing credential-manager extension (1Password, Bitwarden,
    // Dashlane) produces when it intercepts the call and stalls it without ever showing the
    // native OS prompt. Confirmed against the installed package's source, not guessed.
    // The same outcome also happens when the user simply has no passkey for this site yet and
    // the browser has nothing to offer, which is the more common case on the login page, so
    // the sign-in message leads with that rather than only the password-manager explanation.
    if (!webAuthnErr.code && err.message === 'Authentication was not completed') {
        return new AuthClientError(ClientErrorCode.PASSKEY_NO_CREDENTIAL_RETURNED, "No passkey was used. If you haven't set one up yet, sign in with your password first. If you use a password manager extension (1Password, Bitwarden, Dashlane), make sure it's unlocked.", 0);
    }
    if (!webAuthnErr.code && err.message === 'Registration was not completed') {
        return new AuthClientError(ClientErrorCode.PASSKEY_NO_CREDENTIAL_RETURNED, "The passkey wasn't saved. If you use a password manager extension (1Password, Bitwarden, Dashlane), make sure it's unlocked, then try again.", 0);
    }
    switch (webAuthnErr.code) {
        case 'ERROR_CEREMONY_ABORTED':
        case 'ERROR_AUTHENTICATOR_GENERAL_ERROR':
        case 'ERROR_AUTO_REGISTER_USER_VERIFICATION_FAILURE':
            return new AuthClientError(ClientErrorCode.PASSKEY_CANCELLED, 'Passkey prompt was dismissed or timed out', 0);
        case 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED':
            return new AuthClientError(ClientErrorCode.PASSKEY_ALREADY_ON_DEVICE, 'This authenticator already holds a passkey for this account', 0);
        case 'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT':
        case 'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT':
        case 'ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG':
            return new AuthClientError(ClientErrorCode.PASSKEY_AUTHENTICATOR_FULL_OR_UNSUPPORTED, "This security key is full or unsupported. Try a different key or your device's built-in passkey.", 0);
        case 'ERROR_INVALID_DOMAIN':
        case 'ERROR_INVALID_RP_ID':
            return new AuthClientError(ClientErrorCode.PASSKEY_RP_ID_MISMATCH, 'This page origin is not permitted to use the configured relying party', 0);
        case 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY': {
            const causeName = webAuthnErr.cause instanceof Error ? webAuthnErr.cause.name : undefined;
            if (causeName === 'NotAllowedError') {
                return new AuthClientError(ClientErrorCode.PASSKEY_CANCELLED, 'Passkey prompt was dismissed or timed out', 0);
            }
            if (causeName === 'InvalidStateError' && ceremony === 'create') {
                return new AuthClientError(ClientErrorCode.PASSKEY_ALREADY_ON_DEVICE, 'This authenticator already holds a passkey for this account', 0);
            }
            if ((causeName === 'ConstraintError' || causeName === 'NotSupportedError') && ceremony === 'create') {
                return new AuthClientError(ClientErrorCode.PASSKEY_AUTHENTICATOR_FULL_OR_UNSUPPORTED, "This security key is full or unsupported. Try a different key or your device's built-in passkey.", 0);
            }
            if (causeName === 'SecurityError') {
                return new AuthClientError(ClientErrorCode.PASSKEY_RP_ID_MISMATCH, 'This page origin is not permitted to use the configured relying party', 0);
            }
            break;
        }
        default:
            break;
    }
    return new AuthClientError(ClientErrorCode.PASSKEY_UNSUPPORTED, err.message || 'WebAuthn ceremony failed', 0);
}
